import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import {
  ExperimentOutlined,
  ShoppingCartOutlined,
  ScissorOutlined,
  RetweetOutlined,
  PlusCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  EditOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import styles from '../../services/styles';
import { FLOW_COLORS } from '../../components/drum/WizardParts';
import PullToRefresh from '../../components/PullToRefresh';

const { COLORS } = styles;

/**
 * Card-botão touch-friendly reutilizável (DRY): serve para os fluxos e para as
 * ações de conta (perfil / sair). Mantém um único padrão visual e de acessibilidade
 * (`aria-label`, feedback tátil via scale).
 */
const CardButton = ({ label, description, icon, color, onClick, minHeight = 130 }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    style={{
      minHeight,
      border: 'none',
      borderRadius: 16,
      background: color,
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      padding: '16px 12px',
      cursor: 'pointer',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.18)',
      transition: 'transform 0.08s ease, box-shadow 0.08s ease',
      WebkitTapHighlightColor: 'transparent',
    }}
    onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
    onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}
    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
    onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
  >
    <span style={{ fontSize: 40 }}>{icon}</span>
    <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>
      {label}
    </span>
    {description && (
      <span style={{ fontSize: 12, opacity: 0.9, textAlign: 'center' }}>{description}</span>
    )}
  </button>
);

/**
 * Home do PWA dos operadores.
 *
 * Tela central com "cards-botão" grandes, touch-friendly, sem menu lateral e
 * sem header (o layout OperatorLayout é somente conteúdo).
 * - Processamento: todos os operadores.
 * - Conferência de Compra: somente Operador-comprador.
 * - Descarga, Recarga (RC) de hoje e Adicionar RC à máquina: todos os
 *   operadores (em modo mobile).
 * - Meu perfil e Sair: ações de conta, que antes viviam no header/dropdown.
 *
 * A "Retirada de Estoque" é propositalmente ausente — fluxo exclusivo do Admin.
 */
const OperatorHome = () => {
  const navigate = useNavigate();
  const { loggedUser, setLoggedUser } = useContext(GeneralContext);
  const [processChoiceOpen, setProcessChoiceOpen] = useState(false);

  const isOperadorComprador = loggedUser?.type === EUserTypes.OPERADOR_COMPRADOR.code;
  const isLider = loggedUser?.type === EUserTypes.LIDER.code;
  // LIDER herda tudo do Operador e do Operador-comprador (inclusive a Conferência de Compra).
  const canConference = isOperadorComprador || isLider;
  const firstName = (loggedUser?.full_name || loggedUser?.email || '').split(' ')[0];

  // Abre a escolha "Recarga ou Processamento?" antes de ler o tambor.
  // Quem escolhe Recarga vai ao fluxo dedicado (RcConsumptionPanel); quem
  // escolhe Processamento vai para a leitura do tambor (DrumEntry → ProcessDrum).
  const handleProcessCard = () => setProcessChoiceOpen(true);
  const goToRecarga = () => {
    setProcessChoiceOpen(false);
    navigate('/operations/rc-consumption', { state: { color: FLOW_COLORS.rcConsumption } });
  };
  const goToProcessamento = () => {
    setProcessChoiceOpen(false);
    navigate('/entry', { state: { flow: 'process', color: FLOW_COLORS.process } });
  };
  // Recarga de solvente amarelo: mesmo fluxo da recarga de RC, identificada
  // pela origem (source) — desconta do estoque de amarelo em vez do saldo de RC.
  const goToRecargaAmarelo = () => {
    setProcessChoiceOpen(false);
    navigate('/operations/rc-consumption', { state: { color: FLOW_COLORS.yellowConsumption, source: 'YELLOW_SOLVENT' } });
  };

  const buttons = [
    {
      key: 'process',
      label: 'Processamento',
      description: 'Leitura de tambores para a entrada',
      icon: <ExperimentOutlined />,
      color: FLOW_COLORS.process,
      onClick: handleProcessCard,
      visible: true,
    },
    {
      key: 'conference',
      label: 'Conferência de Compra',
      description: 'Leitura de tambores para conferência',
      icon: <ShoppingCartOutlined />,
      color: FLOW_COLORS.conference,
      onClick: () => navigate('/entry', { state: { flow: 'conference', color: FLOW_COLORS.conference } }),
      visible: canConference,
    },
    {
      key: 'lider-totals',
      label: 'Totais do Dia',
      description: 'Por usuário, máquina e geral',
      icon: <BarChartOutlined />,
      color: '#0e7490', // cyan-700: distinto, semântica de relatório
      onClick: () => navigate('/lider-totals', { state: { color: '#0e7490' } }),
      visible: isLider,
    },
    {
      key: 'processed-by-machine',
      label: 'Tambores por máquina',
      description: 'Processados no dia, por máquina',
      icon: <ToolOutlined />,
      color: '#0e7490', // ciano-escuro: consulta/relatório (como Totais do Dia)
      onClick: () => navigate('/drums/processed-by-machine', { state: { color: '#0e7490' } }),
      visible: true,
    },
    {
      key: 'drenagem',
      label: 'Descarga',
      description: 'Registrar descarga do dia',
      icon: <ScissorOutlined />,
      color: FLOW_COLORS.drenagem,
      onClick: () => navigate('/operations/drenagem', { state: { color: FLOW_COLORS.drenagem } }),
      visible: true,
    },
    {
      key: 'amarelamento',
      label: 'Amarelamento',
      description: 'Solvente limpo que virou amarelo',
      icon: <ThunderboltOutlined />,
      color: FLOW_COLORS.amarelamento,
      onClick: () => navigate('/operations/amarelamento', { state: { color: FLOW_COLORS.amarelamento } }),
      visible: true,
    },
    {
      key: 'tanque',
      label: 'Tanque',
      description: 'Informar o total no tanque agora',
      icon: <DatabaseOutlined />,
      color: FLOW_COLORS.tanque,
      onClick: () => navigate('/operations/tanque', { state: { color: FLOW_COLORS.tanque } }),
      visible: true,
    },
    {
      key: 'rc',
      label: 'Recarga (RC)',
      description: 'Saldo de RC de hoje',
      icon: <RetweetOutlined />,
      color: FLOW_COLORS.rc,
      onClick: () => navigate('/operations/rc', { state: { color: FLOW_COLORS.rc } }),
      visible: true,
    },
    {
      key: 'rc-consumption',
      label: 'Adicionar RC à máquina',
      description: 'Registrar adição de RC',
      icon: <PlusCircleOutlined />,
      color: FLOW_COLORS.rcConsumption,
      onClick: () => navigate('/operations/rc-consumption', { state: { color: FLOW_COLORS.rcConsumption } }),
      visible: true,
    },
    {
      key: 'yellow-consumption',
      label: 'Recarga de Amarelo',
      description: 'Amarelo que volta para a máquina',
      icon: <PlusCircleOutlined />,
      color: FLOW_COLORS.yellowConsumption,
      onClick: () => navigate('/operations/rc-consumption', { state: { color: FLOW_COLORS.yellowConsumption, source: 'YELLOW_SOLVENT' } }),
      visible: true,
    },
    {
      key: 'edit-drums',
      label: 'Corrigir tambores',
      description: 'Revisar compra e processamento',
      icon: <EditOutlined />,
      color: FLOW_COLORS.correction,
      onClick: () => navigate('/drums/edit', { state: { color: FLOW_COLORS.correction } }),
      // Correções são do comprador/líder (revisores do dia). As demais
      // correções ficam nos próprios painéis (descarga, RC, tanque etc.).
      visible: canConference,
    },
  ].filter((b) => b.visible);

  // Ações de conta (antes no header/dropdown ProfileOptions).
  const accountButtons = [
    {
      key: 'profile',
      label: 'Meu perfil',
      icon: <UserOutlined />,
      color: '#475569', // cinza-ardósia: neutro, distinto das cores dos fluxos
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogoutOutlined />,
      color: '#b91c1c', // vermelho: semântica de sair/descartar
      onClick: () => setLoggedUser(null),
    },
  ];

  return (
    <PullToRefresh>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Saudação — compacta, em uma linha só, para sobrar espaço aos cards
          de operação (que são o que importa na tela). */}
      <div
        style={{
          background: `linear-gradient(135deg, ${COLORS.PRIMARY}, #2a4a8a)`,
          color: '#fff',
          borderRadius: 12,
          padding: '10px 16px',
          boxShadow: '0 4px 12px rgba(27, 54, 99, 0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Olá, {firstName}
        </span>
        <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'nowrap' }}>
          Escolha uma operação
        </span>
      </div>

      {/* Grid de botões grandes dos fluxos */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 14,
        }}
      >
        {buttons.map((b) => (
          <CardButton
            key={b.key}
            label={b.label}
            description={b.description}
            icon={b.icon}
            color={b.color}
            onClick={b.onClick}
          />
        ))}
      </div>

      {/* Ações de conta */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 14,
        }}
      >
        {accountButtons.map((b) => (
          <CardButton
            key={b.key}
            label={b.label}
            icon={b.icon}
            color={b.color}
            onClick={b.onClick}
            minHeight={96}
          />
        ))}
      </div>

      {/* Escolha inicial do fluxo de Processamento: Recarga ou Processamento.
          A recarga agora é decidida no início (antes de ler o tambor), não no
          final do wizard. */}
      <Modal
        title="O que você vai fazer?"
        open={processChoiceOpen}
        onCancel={() => setProcessChoiceOpen(false)}
        footer={null}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            type="button"
            onClick={goToProcessamento}
            aria-label="Processar um tambor"
            style={{
              minHeight: 96,
              border: 'none',
              borderRadius: 14,
              background: FLOW_COLORS.process,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            <ExperimentOutlined style={{ fontSize: 28 }} />
            <span>Processamento</span>
          </button>
          <button
            type="button"
            onClick={goToRecarga}
            aria-label="Adicionar recarga a uma máquina"
            style={{
              minHeight: 96,
              border: 'none',
              borderRadius: 14,
              background: FLOW_COLORS.rcConsumption,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            <PlusCircleOutlined style={{ fontSize: 28 }} />
            <span>Recarga (RC) à máquina</span>
          </button>
          <button
            type="button"
            onClick={goToRecargaAmarelo}
            aria-label="Adicionar recarga de solvente amarelo a uma máquina"
            style={{
              minHeight: 96,
              border: 'none',
              borderRadius: 14,
              background: FLOW_COLORS.yellowConsumption,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 18px',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            <PlusCircleOutlined style={{ fontSize: 28 }} />
            <span>Recarga de Amarelo</span>
          </button>
        </div>
      </Modal>
    </div>
    </PullToRefresh>
  );
};

export default OperatorHome;
